import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Actions from '../../store/actions';
import { Button, Form } from 'react-bootstrap';
import { Images } from '../../components';
import { useNavigate } from 'react-router-dom';
import { BsCartX } from 'react-icons/bs';
import WithNavBar from '../../Layouts/WithNavBar';
import CustomFooter from '../../components/CustomFooter';
import moment from 'moment';
import { API, graphqlOperation } from 'aws-amplify';
import { deleteCartItem as deleteCartItemMutation } from '../../graphql/mutations';
import { listPackages } from '../../api/queries';

function Cart() {
  const dispatch = useDispatch();
  let navigate = useNavigate();

  // 检查当前域名是否包含 beecadvantage.cn
  const isLocalhost = window.location.hostname.includes('beecadvantage.cn');

  const [currentUserCart, setCurrentUserCart] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [couponCode, setCouponCode] = useState('');

  const [currentUserState, cartItemsState, couponResponse] = useSelector((state) => {
    return [state.auth.cognito, state.cart.listCartItems, state.cart.couponResponse];
  });

  // 根据域名决定使用哪个价格字段
  const getPrice = (packageItem) => {
    if (isLocalhost) {
      // 在 beecadvantage.cn 域名下，如果 cnprice 存在（包括为 0），使用 cnprice，否则使用 price
      return packageItem?.cnprice != null ? packageItem?.cnprice : packageItem?.price;
    }
    return packageItem?.price;
  };

  const pricing = useMemo(() => {
    const type = couponResponse?.type;
    const amount = Number(couponResponse?.amount || 0);
    const minCharge = 0.5;
    const isPercentage = type === 'PERCENTAGE' && Number.isFinite(amount) && amount > 0;
    const isFix = type === 'FIX' && Number.isFinite(amount) && amount > 0;

    const percent = isPercentage ? Math.min(100, Math.max(0, Math.round(amount))) : 0;
    const maxUnitPrice = (currentUserCart || []).reduce((max, item) => {
      const p = Number(getPrice(item?.package) || 0);
      return p > max ? p : max;
    }, 0);

    let discount = 0;
    if (isPercentage && maxUnitPrice > 0) {
      discount = Number((maxUnitPrice * (percent / 100)).toFixed(2));
      discount = Math.min(maxUnitPrice, Math.max(0, discount));
    } else if (isFix && maxUnitPrice > 0) {
      discount = Math.min(maxUnitPrice, Math.max(0, Number(amount.toFixed(2))));
    }

    let grand = Math.max(0, Number((totalPrice - discount).toFixed(2)));
    if (grand > 0 && grand < minCharge) grand = minCharge;
    if (grand > totalPrice) grand = totalPrice;

    discount = Math.max(0, Number((totalPrice - grand).toFixed(2)));
    return { percent, discount, grand, type, maxUnitPrice };
  }, [couponResponse, totalPrice, currentUserCart, isLocalhost]);

  useEffect(() => {
    if (currentUserState) {
      dispatch(Actions.dispatchCartItems(currentUserState.username));
    }
  }, [currentUserState, dispatch]);

  useEffect(() => {
    if (cartItemsState) {
      setCurrentUserCart(cartItemsState);
    }
  }, [cartItemsState]);

  useEffect(() => {
    if (currentUserCart.length > 0) {
      let temp = 0;
      currentUserCart.forEach((item) => {
        const qty = Math.max(1, parseInt(item?.quantity ?? '1', 10) || 1);
        temp += getPrice(item.package) * qty;
      });
      setTotalPrice(temp);
    } else {
      setTotalPrice(0);
    }
  }, [currentUserCart, isLocalhost]);

  const handleDeleteCartItem = (id) => {
    const cartItem = { id };
    const userID = currentUserState?.username;
    dispatch(Actions.dispatchDeleteCartItem(cartItem, userID));
  };

  const applyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    await dispatch(Actions.dispatchVerifyCoupon({ code }, currentUserState?.username));
  };

  const removeCoupon = () => {
    dispatch(Actions.saveCouponResult({}));
    setCouponCode('');
  };

  const getFirstQuizIdFromPackage = (pkg) => {
    return (
      pkg?.packageItems?.items?.[0]?.quiz?.id ||
      pkg?.packageItems?.items?.[0]?.quizId ||
      pkg?.items?.items?.[0]?.quiz?.id ||
      pkg?.items?.items?.[0]?.quizId
    );
  };

  const fetchQuizIdByPackageId = async (packageId) => {
    if (!packageId) return null;
    const result = await API.graphql(
      graphqlOperation(listPackages, { filter: { id: { eq: packageId } }, limit: 1 })
    );
    const pkg = result?.data?.listPackages?.items?.[0];
    return getFirstQuizIdFromPackage(pkg) || null;
  };

  const completeFreeCheckout = async (appliedCouponCode) => {
    try {
      const normalizedCouponCode = (appliedCouponCode || '').trim();
      const promises = currentUserCart.map(async (item) => {
        const packageId = item?.package?.id || item?.packageId;
        const quizId =
          getFirstQuizIdFromPackage(item?.package) || (await fetchQuizIdByPackageId(packageId));
        if (!quizId) {
          throw new Error(`Missing quizId for packageId=${packageId || 'unknown'}`);
        }
        const payload = {
          packageId,
          userId: currentUserState?.username,
          quizId,
          couponCode: normalizedCouponCode ? normalizedCouponCode : undefined,
        };
        return dispatch(Actions.dispatchCreateFreePackage(payload));
      });
      await Promise.all(promises);

      const deletePromises = currentUserCart.map((item) =>
        API.graphql(graphqlOperation(deleteCartItemMutation, { input: { id: item.id } }))
      );
      await Promise.all(deletePromises);

      dispatch(Actions.saveCartData([]));
      removeCoupon();
      navigate('/my-courses?checkoutId=1234');
    } catch (err) {
      console.log('Error creating free packages:', err);
    }
  };

  const checkoutNow = async () => {
    const appliedCouponCode = (couponResponse?.code || couponCode || '').trim();
    const shouldApplyCoupon = !!couponResponse?.code;
    const couponToSend = shouldApplyCoupon ? appliedCouponCode : '';

    if (pricing.grand === 0) {
      await completeFreeCheckout(couponToSend);
      return;
    }

    // 如果总价为 0，跳过 stripe，直接为每个商品创建免费包
    if (totalPrice === 0) {
      await completeFreeCheckout();
      return;
    }
    
    // 总价不为 0，走正常的 stripe 支付流程
    const proceedCheckout = {
      userId: currentUserState?.username,
      subTotal: totalPrice, // 未折扣小计
      total: pricing.grand,
      discount: pricing.discount,
      itemsCount: currentUserCart?.length,
      couponCode: couponToSend,
      origin: window.location.origin,
    };
    console.log('Checkout payload:', proceedCheckout);
    try {
      dispatch(Actions.dispatchCheckout(proceedCheckout));
    } catch (err) {
      console.log('Checkout error:', err);
    }
  };

  const calculateDuration = (minutes) => {
    const duration = moment.duration(minutes, 'minutes');
    return duration;
  };

  return (
    <WithNavBar IsLoggedIn={true}>
      <div className="signup-screen">
        <div className="signup-section cart-section">
          <img src={Images.cartBg} alt="" className="img-fluid w-100 cartBg" />
          <div className="col-xxl-4 col-md-6 mx-auto py-5 mt-4">
            <div className="row mx-0 signup-inner">
              <div className="col px-0 py-4 py-md-0">
                {currentUserCart?.length === 0 ? (
                  <div className="text-center p-5 emptyCart">
                    <img src={Images.cartEmpty} alt="" className="img-fluid mb-4" />
                    <h4 className="heading-4">Your Cart is Empty</h4>
                    <h5 className="heading-5 my-4">Add something to make me happy :)</h5>
                    <a
                      href="#myCoursesSection"
                      className="blue-button col"
                      onClick={() => navigate('/courses')}
                    >
                      Find a Course
                    </a>
                  </div>
                ) : (
                  <div className="d-flex align-items-center">
                    <div className="w-100 py-5">
                      <div className="px-3">
                        <h3 className="heading-3 mb-1">Shopping Cart</h3>
                        <p className="signup-summary body-1">Prices shown in USD</p>
                      </div>

                      <div className="mt-4">
                        <p className="signup-summary body-2 px-3">Course Info</p>
                        {currentUserCart?.map((data, key) => (
                          <div key={key} className="cart-details pb-5 mb-5">
                            <div className="d-flex px-3">
                              <div>
                                <img
                                  src={data?.package?.image}
                                  alt=""
                                  className="cart-course-img"
                                />
                              </div>
                              <div className="ms-3 me-5">
                                <h6 className="heading-6">
                                  {data?.package?.alias
                                    ? data?.package?.alias
                                    : data?.package?.title}
                                </h6>
                                <p className="body-2 signup-summary">
                                  Student: {data?.student?.firstName} {data?.student?.lastName}
                                </p>
                                <p className="body-2 signup-summary">
                                  Expiry:&nbsp;
                                  {calculateDuration(data?.package?.duration)?.years() > 0
                                    ? `${calculateDuration(
                                        data?.package?.duration,
                                      )?.years()} Years `
                                    : ''}
                                  {calculateDuration(data?.package?.duration)?.months() > 0
                                    ? `${calculateDuration(
                                        data?.package?.duration,
                                      )?.months()} Months `
                                    : ''}
                                  {calculateDuration(data?.package?.duration)?.days() > 0
                                    ? `${calculateDuration(data?.package?.duration)?.days()} Days `
                                    : ''}
                                  {calculateDuration(data?.package?.duration)?.hours() > 0
                                    ? `${calculateDuration(
                                        data?.package?.duration,
                                      )?.hours()} Hours `
                                    : ''}
                                  {calculateDuration(data?.package?.duration)?.minutes() > 0
                                    ? `${calculateDuration(
                                        data?.package?.duration,
                                      )?.minutes()} Minutes`
                                    : ''}
                                </p>

                                <div className="mt-4">
                                  <button
                                    className="p-0 m-0 bg-transparent border-0 body-2 cart-links"
                                    onClick={() => handleDeleteCartItem(data.id)}
                                  >
                                    Remove <BsCartX className="ms-1" />
                                  </button>
                                </div>
                              </div>
                              <div className="ms-auto">
                                <h6 className="heading-6">${getPrice(data?.package)}</h6>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="discount-promo mx-3 mt-4 p-3">
                        <div className="d-flex align-items-center gap-3">
                          <Form.Control
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            placeholder="Coupon code"
                          />
                          <Button className="blue-button" onClick={applyCoupon}>
                            Apply
                          </Button>
                          {couponResponse?.code ? (
                            <button
                              className="p-0 m-0 bg-transparent border-0 body-2 cart-links"
                              onClick={removeCoupon}
                              type="button"
                            >
                              Remove
                            </button>
                          ) : null}
                        </div>
                        {couponResponse?.code ? (
                          <div className="mt-2 discount-code">
                            <p className="body-2 m-0">
                              Applied: {couponResponse?.code || couponCode.trim()} (
                              {pricing.type === 'PERCENTAGE'
                                ? `-${pricing.percent}%`
                                : pricing.type === 'FIX'
                                ? `-$${Number(couponResponse?.amount || 0)}`
                                : ''}
                              )
                            </p>
                          </div>
                        ) : null}
                      </div>

                      <div className="promo-details me-4 ms-5 mt-4">
                        <div className="row mx-0">
                          <div className="col-md-6 shopping-total pt-4">
                            <div className="d-flex align-items-baseline">
                              <span className="body-1 subtotal">Subtotal</span>
                              <span className="body-1 subtotal-amount ms-auto">${totalPrice}</span>
                            </div>
                            {pricing.discount > 0 ? (
                              <div className="d-flex align-items-baseline mt-3">
                                <span className="body-1 subtotal">Discount</span>
                                <span className="body-1 subtotal-amount ms-auto">
                                  -${pricing.discount}
                                </span>
                              </div>
                            ) : null}
                            <div className="d-flex align-items-baseline mt-3">
                              <h6 className="heading-6 mb-0 grand">Grand Total:</h6>
                              <h6 className="heading-6 mb-0 grand-amount ms-auto">
                                ${pricing.grand} USD
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="cart-checkout mx-3 pt-4">
                        <div className="d-flex justify-content-between align-items-center gap-4">
                          <a
                            href="#myCoursesSection"
                            onClick={() => navigate('/courses')}
                            className="btn blue-button-outline col mt-4"
                          >
                            Continue Shopping
                          </a>
                          <Button className="blue-button col mt-4" onClick={checkoutNow}>
                            Checkout
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomFooter />
    </WithNavBar>
  );
}

export default Cart;
